import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Square, Download } from 'lucide-react';
import { EditorToolbar } from './EditorToolbar';
import { LayersPanel } from './LayersPanel';
import { SocialCanvasWorkspace } from './SocialCanvasWorkspace';
import { SocialPropertyPanel } from './SocialPropertyPanel';
import { useHistory } from '../../canvas-shared/useHistory';
import { createDefaultElement } from './canvasTypes';
import { exportDesign } from './exportUtils';
import { ExportBatchModal } from './ExportBatchModal';
import { CanvasOnboarding } from '../../canvas-shared/CanvasOnboarding';
import { flattenRawData } from '../../canvas-shared/canvasUtils';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';
import { socialPostingService } from '../../../services/socialPosting';
import { resolveProductField } from '../ProductCard';
import type { SocialCanvasElement, SocialCanvasElementType, SocialPostDesign, SocialPostProduct } from '../../../services/socialPosting';
import type { BreadcrumbSegment } from '../../canvas-shared/CanvasBreadcrumb';

// ── Component ───────────────────────────────────────────────

interface SocialCanvasEditorProps {
  postId: string;
  design: SocialPostDesign;
  products: SocialPostProduct[];
  onBack: () => void;
  onDesignUpdated: (design: SocialPostDesign) => void;
  breadcrumbSegments?: BreadcrumbSegment[];
}

export function SocialCanvasEditor({
  postId,
  design,
  products,
  onBack,
  onDesignUpdated,
  breadcrumbSegments,
}: SocialCanvasEditorProps) {
  const history = useHistory<SocialCanvasElement[]>([...design.elements], 50);
  const elements = history.state;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [designName, setDesignName] = useState(design.name);
  const [canvasWidth, setCanvasWidth] = useState(design.width);
  const [canvasHeight, setCanvasHeight] = useState(design.height);
  const [zoom, setZoom] = useState(0.75);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(0);

  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveRef = useRef<NodeJS.Timeout | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showGrid, setShowGrid] = useState(false);
  const { toast } = useToast();

  // Sample data from first product
  const sampleData = products.length > 0 ? flattenRawData(products[selectedProductIndex]?.raw_data || {}) : {};

  // Detected fields from products
  const detectedFields = products.length > 0 ? Object.keys(products[0].raw_data) : [];

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    };
  }, []);

  // Auto-save with 5s debounce
  useEffect(() => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      handleSave(true);
    }, 5000);
    return () => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    };
  }, [elements, designName, canvasWidth, canvasHeight]);

  // ── Element operations ────────────────────────────────────

  const addElement = useCallback((type: SocialCanvasElementType) => {
    const el = createDefaultElement(type);
    history.set([...elements, el]);
    setSelectedIndex(elements.length);
    toast({ title: `${type.charAt(0).toUpperCase() + type.slice(1)} added` });
  }, [elements, history, toast]);

  const updateElement = useCallback((index: number, updates: Partial<SocialCanvasElement>) => {
    history.set(
      elements.map((el, i) => (i === index ? ({ ...el, ...updates } as SocialCanvasElement) : el))
    );
  }, [elements, history]);

  const deleteElement = useCallback((index: number) => {
    history.set(elements.filter((_, i) => i !== index));
    setSelectedIndex(null);
    toast({ title: 'Element deleted' });
  }, [elements, history, toast]);

  const moveElementLayer = useCallback((index: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? index + 1 : index - 1;
    if (targetIdx < 0 || targetIdx >= elements.length) return;
    const next = [...elements];
    const temp = next[index];
    next[index] = next[targetIdx];
    next[targetIdx] = temp;
    history.set(next);
    setSelectedIndex(targetIdx);
  }, [elements, history]);

  const reorderElement = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const next = [...elements];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    history.set(next);
    setSelectedIndex(toIndex);
  }, [elements, history]);

  const duplicateElement = useCallback((index: number) => {
    const el = elements[index];
    const copy = { ...el, id: `el_${Date.now()}`, x: el.x + 20, y: el.y + 20 };
    history.set([...elements, copy as SocialCanvasElement]);
    setSelectedIndex(elements.length);
    toast({ title: 'Element duplicated' });
  }, [elements, history, toast]);

  // ── Alignment ─────────────────────────────────────────────

  const handleAlign = useCallback((alignment: string) => {
    if (selectedIndex === null) return;
    const el = elements[selectedIndex];
    const updates: Partial<SocialCanvasElement> = {};

    switch (alignment) {
      case 'left': updates.x = 0; break;
      case 'center-h': updates.x = Math.round((canvasWidth - el.width) / 2); break;
      case 'right': updates.x = canvasWidth - el.width; break;
      case 'top': updates.y = 0; break;
      case 'center-v': updates.y = Math.round((canvasHeight - el.height) / 2); break;
      case 'bottom': updates.y = canvasHeight - el.height; break;
    }

    updateElement(selectedIndex, updates);
  }, [selectedIndex, elements, canvasWidth, canvasHeight, updateElement]);

  // ── Save ──────────────────────────────────────────────────

  const handleSave = async (silent = false) => {
    try {
      if (!silent) setIsSaving(true);
      const updated = await socialPostingService.updateDesign(postId, design.id, {
        name: designName,
        elements,
        width: canvasWidth,
        height: canvasHeight,
      });
      onDesignUpdated(updated);
      if (!silent) {
        setSaveSuccess(true);
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch {
      // handled
    } finally {
      if (!silent) setIsSaving(false);
    }
  };

  // ── Format change ─────────────────────────────────────────

  const handleFormatChange = useCallback((w: number, h: number) => {
    setCanvasWidth(w);
    setCanvasHeight(h);
  }, []);

  // ── Export ──────────────────────────────────────────────────

  const handleExport = async () => {
    // Save first so the backend has the latest elements
    await handleSave(true);
    try {
      setIsExporting(true);
      const currentProduct = products[selectedProductIndex];
      await exportDesign(postId, design.id, designName || 'design', {
        format: 'png',
        quality: 1,
        scale: 2,
        productId: currentProduct?.id,
      });
    } catch (err) {
      console.error('Export failed:', err);
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsExporting(false);
    }
  };

  // ── Preview ─────────────────────────────────────────────────

  const handlePreview = async () => {
    await handleSave(true);
    try {
      setIsPreviewing(true);
      const currentProduct = products[selectedProductIndex];
      const blob = await socialPostingService.exportDesign(postId, design.id, {
        format: 'png',
        quality: 100,
        scale: 2,
        productId: currentProduct?.id,
      });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err) {
      console.error('Preview failed:', err);
      alert(`Preview failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handleDownloadFromPreview = () => {
    if (!previewUrl) return;
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = `${designName || 'design'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Export All (batch — opens modal, async via BullMQ) ────

  const handleOpenExportModal = async () => {
    await handleSave(true);
    setShowExportModal(true);
  };

  // ── Keyboard shortcuts ────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept when typing in inputs
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;

      // Delete / Backspace
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIndex !== null) {
        e.preventDefault();
        deleteElement(selectedIndex);
        return;
      }

      // Ctrl+D — duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedIndex !== null) {
        e.preventDefault();
        duplicateElement(selectedIndex);
        return;
      }

      // Ctrl+Z — undo / Ctrl+Shift+Z — redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) { history.redo(); } else { history.undo(); }
        return;
      }

      // Ctrl+S — save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }

      // Arrow keys — nudge
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selectedIndex !== null) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const el = elements[selectedIndex];
        const updates: Partial<SocialCanvasElement> = {};
        switch (e.key) {
          case 'ArrowUp': updates.y = el.y - step; break;
          case 'ArrowDown': updates.y = el.y + step; break;
          case 'ArrowLeft': updates.x = el.x - step; break;
          case 'ArrowRight': updates.x = el.x + step; break;
        }
        updateElement(selectedIndex, updates);
        return;
      }

      // Escape — exit fullscreen or deselect
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          setSelectedIndex(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, elements, history, deleteElement, duplicateElement, updateElement, isFullscreen]);

  const selectedElement = selectedIndex !== null ? elements[selectedIndex] : null;

  return (
    <div className={`flex flex-col bg-background overflow-hidden ${
      isFullscreen
        ? 'fixed inset-0 z-50'
        : 'h-[calc(100vh-8rem)] rounded-xl border-2 shadow-lg'
    }`}>
      {/* Toolbar */}
      <EditorToolbar
        designName={designName}
        onDesignNameChange={setDesignName}
        zoom={zoom}
        onZoomChange={setZoom}
        onAddElement={addElement}
        onUndo={history.undo}
        onRedo={history.redo}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
        onSave={() => handleSave()}
        isSaving={isSaving}
        saveSuccess={saveSuccess}
        onPreview={handlePreview}
        isPreviewing={isPreviewing}
        onExportAll={handleOpenExportModal}
        productCount={products.length}
        onBack={onBack}
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
        onFormatChange={handleFormatChange}
        selectedIndex={selectedIndex}
        onAlign={handleAlign}
        isFullscreen={isFullscreen}
        onToggleFullscreen={() => setIsFullscreen((f) => !f)}
        showGrid={showGrid}
        onToggleGrid={() => setShowGrid((g) => !g)}
        breadcrumbSegments={breadcrumbSegments}
      />

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Layers panel */}
        <LayersPanel
          elements={elements}
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
          onUpdateElement={updateElement}
          onMoveLayer={moveElementLayer}
          onReorderLayer={reorderElement}
        />

        {/* Canvas workspace */}
        <SocialCanvasWorkspace
          elements={elements}
          selectedIndex={selectedIndex}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          zoom={zoom}
          isPreview={true}
          sampleData={sampleData}
          showGrid={showGrid}
          onSelectElement={setSelectedIndex}
          onUpdateElement={updateElement}
          onAddElement={addElement}
        />

        {/* Property panel */}
        <div className="w-[300px] shrink-0 border-l bg-background overflow-y-auto">
          {selectedElement ? (
            <SocialPropertyPanel
              element={selectedElement}
              detectedFields={detectedFields}
              sampleData={sampleData}
              onUpdate={(updates) => updateElement(selectedIndex!, updates)}
              onDelete={() => deleteElement(selectedIndex!)}
              onMoveLayer={(dir) => moveElementLayer(selectedIndex!, dir)}
              onDuplicate={() => duplicateElement(selectedIndex!)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground px-6 gap-4">
              <div className="text-center">
                <p className="text-sm font-medium">No element selected</p>
                <p className="text-xs mt-1">Click an element on the canvas to edit it</p>
              </div>
              <div className="border rounded-lg p-3 w-full bg-muted/20">
                <p className="text-xs font-medium mb-2">Keyboard Shortcuts</p>
                <div className="grid grid-cols-2 gap-1 text-[10px]">
                  <span className="text-muted-foreground">Ctrl+Z</span><span>Undo</span>
                  <span className="text-muted-foreground">Ctrl+Shift+Z</span><span>Redo</span>
                  <span className="text-muted-foreground">Ctrl+D</span><span>Duplicate</span>
                  <span className="text-muted-foreground">Ctrl+S</span><span>Save</span>
                  <span className="text-muted-foreground">Delete</span><span>Remove</span>
                  <span className="text-muted-foreground">Arrows</span><span>Nudge (1px)</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status bar */}
      <div className="shrink-0 border-t bg-muted/20 px-4 py-1.5 flex items-center gap-4 text-xs text-muted-foreground">
        <span>{canvasWidth} x {canvasHeight}</span>
        <span>Zoom: {Math.round(zoom * 100)}%</span>
        <span>{elements.length} element{elements.length !== 1 ? 's' : ''}</span>
        {products.length > 0 && (
          <>
            <div className="flex-1" />
            <Select
              value={String(selectedProductIndex)}
              onValueChange={(v) => {
                const idx = Number(v);
                setSelectedProductIndex(idx);
                // Clear removeBg on token images — cached bgRemovedSrc is product-specific
                const hasTokenBg = elements.some((el) => el.type === 'image' && el.removeBg && /\{\{[^}]+\}\}/.test(el.src));
                if (hasTokenBg) {
                  history.set(elements.map((el) => {
                    if (el.type === 'image' && el.removeBg && /\{\{[^}]+\}\}/.test(el.src)) {
                      return { ...el, removeBg: false, bgRemovedSrc: undefined } as typeof el;
                    }
                    return el;
                  }));
                }
              }}
            >
              <SelectTrigger className="h-6 w-[200px] border bg-transparent rounded px-1 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {products.map((p, i) => (
                  <SelectItem key={p.id} value={String(i)}>
                    Product #{i + 1} — {resolveProductField(p.raw_data, 'title').slice(0, 30) || `ID: ${p.feed_product_id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {/* Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={handleClosePreview}>
          <div className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={previewUrl}
              alt="Export preview"
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={handleDownloadFromPreview}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                <Download className="h-4 w-4" />
                Download
              </button>
              <button
                onClick={handleClosePreview}
                className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Batch Modal */}
      <ExportBatchModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        products={products}
        postId={postId}
        designId={design.id}
        designName={designName}
      />

      {/* Onboarding */}
      <CanvasOnboarding />
    </div>
  );
}
